import { useEffect, useRef, useState, type KeyboardEvent, type PointerEvent } from "react";
import type { Annotation, ImageAnnotation, InkStroke, ShapeAnnotation, TextAnnotation } from "@deckloom/core/schema";
import { rgba } from "./fills";
import { usePlayer } from "./context";

export type ToolKind = "draw" | "shape" | "text" | "eraser";
export type ShapeKind = ShapeAnnotation["shape"];

/** The active annotation tool and its options (all in slide pt). */
export interface InkTool {
  kind: ToolKind;
  color: { hex: string; alpha: number };
  /** stroke width for draw/shape */
  width: number;
  shape: ShapeKind;
  fill: boolean;
  /** font size for text */
  fontSize: number;
}

export const DEFAULT_TOOL: InkTool = { kind: "draw", color: { hex: "#e11d48", alpha: 1 }, width: 3, shape: "rect", fill: false, fontSize: 18 };

/** The eraser's cursor: Lucide's eraser (white, a dark edge under it, so it reads on any slide), its lower-left corner the hotspot. */
const ERASER_PATHS = '<path d="M21 21H8a2 2 0 0 1-1.42-.587l-3.994-3.999a2 2 0 0 1 0-2.828l10-10a2 2 0 0 1 2.829 0l5.999 6a2 2 0 0 1 0 2.828L12.834 21"/><path d="m5.082 11.09 8.828 8.828"/>';
const ERASER_SVG = `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke-linecap="round" stroke-linejoin="round"><g stroke="#000" stroke-opacity=".6" stroke-width="4">${ERASER_PATHS}</g><g stroke="#fff" stroke-width="2">${ERASER_PATHS}</g></svg>`;
const ERASER_CURSOR = `url("data:image/svg+xml,${encodeURIComponent(ERASER_SVG)}") 4 20, cell`;

export interface InkLayerProps {
  strokes: Annotation[];
  /** unpublished drawings of this user: drawn faded above the published ones; the eraser removes them too */
  drafts?: Annotation[];
  /** when set, the layer captures pointer events and emits annotations */
  tool?: InkTool | null;
  authorId: string;
  onStroke?: (a: Annotation) => void;
  onRemove?: (id: string) => void;
}

export function strokePath(points: number[]): string {
  if (points.length < 3) return "";
  let d = `M${points[0]},${points[1]}`;
  if (points.length < 6) return `${d} L${points[0]! + 0.01},${points[1]}`;
  for (let i = 3; i < points.length - 3; i += 3) {
    const x = points[i]!, y = points[i + 1]!, nx = points[i + 3]!, ny = points[i + 4]!;
    d += ` Q${x},${y} ${(x + nx) / 2},${(y + ny) / 2}`;
  }
  const n = points.length;
  d += ` L${points[n - 3]},${points[n - 2]}`;
  return d;
}

/** Translucent strokes (the marker) blend like a highlighter. */
function strokeOpacity(s: InkStroke): number {
  if (s.tool === "highlighter") return Math.min(s.color.alpha, 0.4);
  return s.color.alpha;
}

function StrokeView({ s, erasable, onErase }: { s: InkStroke; erasable?: boolean; onErase?: () => void }) {
  const op = strokeOpacity(s);
  return (
    <path
      d={strokePath(s.points)}
      fill="none"
      stroke={s.color.hex}
      strokeWidth={s.width}
      strokeLinecap="round"
      strokeLinejoin="round"
      opacity={op}
      style={{ mixBlendMode: op < 1 ? "multiply" : undefined, pointerEvents: erasable ? "visibleStroke" : "none" }}
      onPointerDown={erasable ? (e) => { e.stopPropagation(); onErase?.(); } : undefined}
      onPointerEnter={erasable ? (e) => { if (e.buttons) onErase?.(); } : undefined}
    />
  );
}

function ShapeView({ s, erasable, onErase }: { s: ShapeAnnotation; erasable?: boolean; onErase?: () => void }) {
  const color = s.color.hex, op = s.color.alpha;
  const common = {
    stroke: color,
    strokeWidth: s.width,
    opacity: op,
    style: { pointerEvents: erasable ? ("visiblePainted" as const) : ("none" as const) },
    onPointerDown: erasable ? (e: PointerEvent) => { e.stopPropagation(); onErase?.(); } : undefined,
    onPointerEnter: erasable ? (e: PointerEvent) => { if (e.buttons) onErase?.(); } : undefined,
  };
  switch (s.shape) {
    case "rect": {
      const x = Math.min(s.x, s.x + s.w), y = Math.min(s.y, s.y + s.h);
      return <rect x={x} y={y} width={Math.abs(s.w)} height={Math.abs(s.h)} rx={s.width} fill={s.fill ? color : "none"} fillOpacity={s.fill ? 0.25 : undefined} {...common} />;
    }
    case "ellipse":
      return <ellipse cx={s.x + s.w / 2} cy={s.y + s.h / 2} rx={Math.abs(s.w) / 2} ry={Math.abs(s.h) / 2} fill={s.fill ? color : "none"} fillOpacity={s.fill ? 0.25 : undefined} {...common} />;
    case "line":
      return <line x1={s.x} y1={s.y} x2={s.x + s.w} y2={s.y + s.h} strokeLinecap="round" {...common} />;
    case "arrow": {
      const len = Math.hypot(s.w, s.h) || 1;
      const head = Math.max(8, s.width * 4);
      const ux = s.w / len, uy = s.h / len;
      const ex = s.x + s.w, ey = s.y + s.h;
      const bx = ex - ux * head, by = ey - uy * head;
      const px = -uy * head * 0.45, py = ux * head * 0.45;
      return (
        <g {...common} strokeLinecap="round" strokeLinejoin="round">
          <line x1={s.x} y1={s.y} x2={bx} y2={by} />
          <path d={`M${bx + px},${by + py} L${ex},${ey} L${bx - px},${by - py} Z`} fill={color} />
        </g>
      );
    }
  }
}

function TextView({ s, erasable, onErase }: { s: TextAnnotation; erasable?: boolean; onErase?: () => void }) {
  return (
    <foreignObject x={s.x} y={s.y} width={s.w} height={s.size * 1.3 * (s.text.split("\n").length + 4)} style={{ overflow: "visible", pointerEvents: erasable ? "auto" : "none" }}>
      <div
        style={{ fontFamily: "system-ui, sans-serif", fontSize: `${s.size}px`, lineHeight: 1.3, color: rgba(s.color), whiteSpace: "pre-wrap", overflowWrap: "break-word", width: "fit-content", maxWidth: "100%", textShadow: "0 0 2px rgba(255,255,255,0.8)" }}
        onPointerDown={erasable ? (e) => { e.stopPropagation(); onErase?.(); } : undefined}
      >
        {s.text}
      </div>
    </foreignObject>
  );
}

/** Images come from the draw API. As an SVG <image>, an SVG file is drawn like a picture and its scripts never run. */
function ImageView({ s, erasable, onErase }: { s: ImageAnnotation; erasable?: boolean; onErase?: () => void }) {
  return (
    <image
      href={s.src}
      x={s.x}
      y={s.y}
      width={s.w}
      height={s.h}
      preserveAspectRatio="xMidYMid meet"
      style={{ pointerEvents: erasable ? "auto" : "none" }}
      onPointerDown={erasable ? (e) => { e.stopPropagation(); onErase?.(); } : undefined}
    />
  );
}

function AnnotationView({ a, erasable, onErase }: { a: Annotation; erasable?: boolean; onErase?: () => void }) {
  switch (a.kind) {
    case "stroke": return <StrokeView s={a} erasable={erasable} onErase={onErase} />;
    case "shape": return <ShapeView s={a} erasable={erasable} onErase={onErase} />;
    case "text": return <TextView s={a} erasable={erasable} onErase={onErase} />;
    case "image": return <ImageView s={a} erasable={erasable} onErase={onErase} />;
  }
}

/** Drawing overlay in slide units. Mounted above the slide SVG. */
export function InkLayer({ strokes, drafts, tool, authorId, onStroke, onRemove }: InkLayerProps) {
  const { deck } = usePlayer();
  const svgRef = useRef<SVGSVGElement>(null);
  const [draft, setDraft] = useState<number[] | null>(null);
  const [shapeDraft, setShapeDraft] = useState<{ x: number; y: number; w: number; h: number } | null>(null);
  const [textDraft, setTextDraft] = useState<{ x: number; y: number; text: string } | null>(null);
  const textRef = useRef<HTMLTextAreaElement>(null);
  const active = Boolean(tool);
  const kind = tool?.kind;

  useEffect(() => { if (textDraft) textRef.current?.focus(); }, [textDraft]);

  const toSlide = (e: PointerEvent<SVGSVGElement>): [number, number] => {
    const svg = svgRef.current!;
    const rect = svg.getBoundingClientRect();
    const scale = Math.min(rect.width / deck.size.w, rect.height / deck.size.h);
    const ox = (rect.width - deck.size.w * scale) / 2, oy = (rect.height - deck.size.h * scale) / 2;
    return [(e.clientX - rect.left - ox) / scale, (e.clientY - rect.top - oy) / scale];
  };

  const base = () => ({ id: crypto.randomUUID(), authorId, createdAt: Date.now() });

  const commitText = () => {
    if (!tool || !textDraft) return;
    const text = textDraft.text.trim();
    setTextDraft(null);
    if (!text) return;
    onStroke?.({ kind: "text", ...base(), x: textDraft.x, y: textDraft.y, w: Math.max(60, deck.size.w - textDraft.x - 4), text, color: tool.color, size: tool.fontSize });
  };

  const onDown = (e: PointerEvent<SVGSVGElement>) => {
    if (!tool) return;
    const [x, y] = toSlide(e);
    switch (tool.kind) {
      case "draw":
        e.currentTarget.setPointerCapture(e.pointerId);
        setDraft([x, y, e.pressure || 0.5]);
        break;
      case "shape":
        e.currentTarget.setPointerCapture(e.pointerId);
        setShapeDraft({ x, y, w: 0, h: 0 });
        break;
      case "text":
        // cancelling pointerdown stops the following mousedown from moving focus away from the textarea
        e.preventDefault();
        if (textDraft) commitText();
        else setTextDraft({ x, y, text: "" });
        break;
      case "eraser":
        break;
    }
  };
  const onMove = (e: PointerEvent<SVGSVGElement>) => {
    if (!tool) return;
    if (tool.kind === "draw" && draft) {
      const [x, y] = toSlide(e);
      setDraft((d) => (d ? [...d, x, y, e.pressure || 0.5] : d));
    } else if (tool.kind === "shape" && shapeDraft) {
      const [x, y] = toSlide(e);
      const shift = e.shiftKey;
      setShapeDraft((d) => {
        if (!d) return d;
        let w = x - d.x, h = y - d.y;
        if (shift && (tool.shape === "rect" || tool.shape === "ellipse")) { const m = Math.max(Math.abs(w), Math.abs(h)); w = Math.sign(w || 1) * m; h = Math.sign(h || 1) * m; }
        return { ...d, w, h };
      });
    }
  };
  const onUp = (e: PointerEvent<SVGSVGElement>) => {
    if (!tool) return;
    if (tool.kind === "draw" && draft) {
      e.currentTarget.releasePointerCapture(e.pointerId);
      const stroke: InkStroke = { kind: "stroke", ...base(), color: tool.color, width: tool.width, points: draft };
      setDraft(null);
      onStroke?.(stroke);
    } else if (tool.kind === "shape" && shapeDraft) {
      e.currentTarget.releasePointerCapture(e.pointerId);
      const d = shapeDraft;
      setShapeDraft(null);
      if (Math.abs(d.w) < 2 && Math.abs(d.h) < 2) return;
      const norm = tool.shape === "rect" || tool.shape === "ellipse";
      onStroke?.({
        kind: "shape", ...base(), shape: tool.shape,
        x: norm ? Math.min(d.x, d.x + d.w) : d.x, y: norm ? Math.min(d.y, d.y + d.h) : d.y,
        w: norm ? Math.abs(d.w) : d.w, h: norm ? Math.abs(d.h) : d.h,
        color: tool.color, width: tool.width, fill: tool.fill,
      });
    }
  };

  const onTextKey = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); commitText(); }
    else if (e.key === "Escape") setTextDraft(null);
    e.stopPropagation();
  };

  const cursor = !active ? "default" : kind === "eraser" ? ERASER_CURSOR : kind === "text" ? "text" : "crosshair";
  const erasable = kind === "eraser";
  return (
    <svg
      ref={svgRef}
      className="ink"
      viewBox={`0 0 ${deck.size.w} ${deck.size.h}`}
      preserveAspectRatio="xMidYMid meet"
      style={{ position: "absolute", inset: 0, width: "100%", height: "100%", pointerEvents: active ? "auto" : "none", touchAction: active ? "none" : "auto", cursor }}
      onPointerDown={onDown}
      onPointerMove={onMove}
      onPointerUp={onUp}
      onPointerCancel={onUp}
    >
      {strokes.map((a) => <AnnotationView key={a.id} a={a} erasable={erasable} onErase={() => onRemove?.(a.id)} />)}
      {drafts && drafts.length > 0 && (
        <g className="ink-drafts" opacity={0.55}>
          {drafts.map((a) => <AnnotationView key={a.id} a={a} erasable={erasable} onErase={() => onRemove?.(a.id)} />)}
        </g>
      )}
      {draft && tool && <StrokeView s={{ kind: "stroke", id: "draft", authorId, createdAt: 0, color: tool.color, width: tool.width, points: draft }} />}
      {shapeDraft && tool && (
        <ShapeView s={{ kind: "shape", id: "draft", authorId, createdAt: 0, shape: tool.shape, ...shapeDraft, color: tool.color, width: tool.width, fill: tool.fill }} />
      )}
      {textDraft && tool && (
        <foreignObject x={textDraft.x} y={textDraft.y} width={Math.max(120, deck.size.w - textDraft.x - 4)} height={tool.fontSize * 1.3 * 4} style={{ overflow: "visible" }}>
          <textarea
            ref={textRef}
            value={textDraft.text}
            rows={1}
            placeholder="Type, Enter to place"
            onChange={(e) => setTextDraft((d) => (d ? { ...d, text: e.target.value } : d))}
            onKeyDown={onTextKey}
            onBlur={commitText}
            onPointerDown={(e) => e.stopPropagation()}
            style={{ fontFamily: "system-ui, sans-serif", fontSize: `${tool.fontSize}px`, lineHeight: 1.3, color: rgba(tool.color), background: "rgba(255,255,255,0.85)", border: `1px dashed ${tool.color.hex}`, outline: "none", padding: 0, margin: 0, width: "60%", minWidth: 120, resize: "none", overflow: "hidden" }}
          />
        </foreignObject>
      )}
    </svg>
  );
}
